import { useCallback, useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONVERSATIONS } from "@/constants/testIds";

/** Fix AH (2026-10-21): llamadas de WhatsApp entrantes. Recepcion y la
 * administracion ven el aviso en cualquier pantalla y atienden desde el
 * navegador (microfono + WebRTC). El navegador arma la respuesta al audio que
 * ofrece WhatsApp; el servidor se la manda a Meta (pre_accept) y, cuando el
 * audio conecta, acepta la llamada. Una sola persona toma cada llamada. */
const POLL_MS = 2000;
const ICE_WAIT_MS = 3000;
const CONNECT_TIMEOUT_MS = 20000;
const STAFF_ROLES = ["admin", "reception"];
const CALL_STATE_TEXT = { closing: "Colgando...", close_failed: "La llamada sigue abierta" };

function playRing(ctxRef) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!ctxRef.current) ctxRef.current = new Ctx();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    [0, 0.25].forEach((t) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + 0.22);
    });
  } catch {
    // sin audio: queda el aviso en pantalla
  }
}

function waitForIce(pc) {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => { pc.removeEventListener("icegatheringstatechange", check); resolve(); };
    const check = () => { if (pc.iceGatheringState === "complete") done(); };
    pc.addEventListener("icegatheringstatechange", check);
    setTimeout(done, ICE_WAIT_MS);
  });
}

function fmt(seconds) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function IncomingCallWidget() {
  const { user, onBeforeLogout } = useAuth();
  const enabled = Boolean(user && STAFF_ROLES.includes(user.role));
  const [ringing, setRinging] = useState([]);
  const [active, setActive] = useState(null); // {id, name, phone, state: "connecting" | "in_call"}
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const activeRef = useRef(null);
  const attemptRef = useRef(null); // intento de atender en curso: {id, cancelled, timer, audio}
  const closingRef = useRef(false); // hay un hangup en vuelo: no se duplica ni lo cierra el sondeo antes de saber el resultado
  activeRef.current = active;

  // Suelta microfono, audio y conexion y cancela el intento en curso (no toca el estado de React).
  // Lo que resuelva despues (permiso, ICE, respuestas de la API) ve attempt.cancelled y se descarta.
  const release = useCallback(() => {
    const attempt = attemptRef.current;
    attemptRef.current = null;
    if (attempt) { attempt.cancelled = true; clearTimeout(attempt.timer); }
    if (pcRef.current) { try { pcRef.current.close(); } catch { /* ya cerrada */ } }
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    const audioEl = (attempt && attempt.audio) || audioRef.current; // al desmontar, audioRef ya viene en null
    if (audioEl) {
      try { audioEl.pause(); } catch { /* sin audio */ }
      audioEl.srcObject = null;
    }
    pcRef.current = null;
    streamRef.current = null;
  }, []);

  const cleanup = useCallback(() => {
    release();
    setActive(null);
    setMuted(false);
    setSeconds(0);
  }, [release]);

  // Suelta todo y, si habia una llamada en curso, avisa al servidor (mejor esfuerzo): si todavia suena la
  // rechaza y si ya la tomo esta persona la termina
  const abandon = useCallback(() => {
    const id = attemptRef.current?.id || activeRef.current?.id;
    const inFlight = closingRef.current;
    cleanup();
    if (id && !inFlight) api.post(`/calls/${id}/hangup`).catch(() => {});
  }, [cleanup]);

  // Al salir de pantalla (cierre de sesion): microfono, audio y conexion no pueden quedar vivos
  useEffect(() => () => {
    abandon();
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch { /* ya cerrado */ } audioCtxRef.current = null; }
  }, [abandon]);

  // Si deja de estar habilitado con la pantalla montada (sesion cerrada o cambio de rol)
  useEffect(() => {
    if (!enabled) abandon();
  }, [enabled, abandon]);

  // Cerrar sesion: la llamada se corta ANTES de que el servidor borre la sesion (despues el hangup daria 401).
  // Microfono, audio y conexion se sueltan al instante; la espera al servidor tiene tope en el logout.
  useEffect(() => {
    if (!enabled || !onBeforeLogout) return undefined;
    return onBeforeLogout(async () => {
      const id = attemptRef.current?.id || activeRef.current?.id;
      const inFlight = closingRef.current;
      cleanup();
      if (!id || inFlight) return;
      try {
        await api.post(`/calls/${id}/hangup`);
      } catch {
        // mejor esfuerzo: si Meta no la corto, la termina cuando se corta el audio
      }
    });
  }, [enabled, onBeforeLogout, cleanup]);

  // llamadas sonando (solo mientras no hay una en curso)
  useEffect(() => {
    if (!enabled) return undefined;
    let stop = false;
    const poll = async () => {
      if (activeRef.current) return;
      try {
        const { data } = await api.get("/calls/incoming");
        if (!stop) setRinging(data);
      } catch {
        // se reintenta en el proximo ciclo
      }
    };
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => { stop = true; clearInterval(timer); };
  }, [enabled]);

  // timbre mientras suena
  useEffect(() => {
    if (!ringing.length || active) return undefined;
    playRing(audioCtxRef);
    const timer = setInterval(() => playRing(audioCtxRef), 2000);
    return () => clearInterval(timer);
  }, [ringing.length, active]);

  // llamada en curso: tiempo y fin desde el otro lado
  useEffect(() => {
    if (!active) return undefined;
    const tick = setInterval(() => setSeconds((s) => (activeRef.current?.state === "in_call" ? s + 1 : s)), 1000);
    const attempt = attemptRef.current;
    const watch = setInterval(async () => {
      try {
        const { data } = await api.get(`/calls/${active.id}`);
        // "ringing": el servidor todavia no la tomo (el permiso del microfono o ICE pueden tardar mas que este sondeo)
        if (!closingRef.current && attemptRef.current === attempt && !["ringing", "answering", "in_progress"].includes(data.status)) {
          if (activeRef.current?.state === "close_failed") setError(""); // la cerro el otro lado: no queda nada por resolver
          cleanup();
        }
      } catch {
        // se reintenta
      }
    }, POLL_MS);
    return () => { clearInterval(tick); clearInterval(watch); };
  }, [active, cleanup]);

  const answer = async (call) => {
    setError("");
    setRinging([]);
    // Colgar o salir de pantalla cancela el intento: cada espera revisa attempt.cancelled y suelta lo que le llegue
    const attempt = { id: call.id, cancelled: false, timer: null };
    attemptRef.current = attempt;
    setActive({ id: call.id, name: call.name, phone: call.phone, state: "connecting" });
    const hangupLate = () => api.post(`/calls/${call.id}/hangup`).catch(() => {});
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (attempt.cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      const pc = new window.RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.ontrack = (e) => {
        const el = audioRef.current;
        if (attempt.cancelled || !el) return;
        attempt.audio = el;
        el.srcObject = e.streams[0];
        el.play().catch(() => {});
      };
      let accepted = false;
      pc.onconnectionstatechange = async () => {
        if (attempt.cancelled) return;
        if (pc.connectionState === "connected" && !accepted) {
          accepted = true;
          try {
            await api.post(`/calls/${call.id}/accept`);
            if (attempt.cancelled) { hangupLate(); return; }
            setActive((a) => (a ? { ...a, state: "in_call" } : a));
          } catch (err) {
            if (attempt.cancelled) return;
            setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo atender la llamada");
            cleanup();
          }
        } else if (["failed", "closed"].includes(pc.connectionState)) {
          cleanup();
        }
      };
      await pc.setRemoteDescription({ type: "offer", sdp: call.offer_sdp });
      if (attempt.cancelled) return;
      await pc.setLocalDescription(await pc.createAnswer());
      if (attempt.cancelled) return;
      await waitForIce(pc);
      if (attempt.cancelled) return;
      await api.post(`/calls/${call.id}/answer`, { sdp: pc.localDescription.sdp });
      if (attempt.cancelled) { hangupLate(); return; }
      attempt.timer = setTimeout(() => {
        if (attempt.cancelled || activeRef.current?.state !== "connecting") return;
        setError("No se pudo conectar el audio de la llamada");
        hangupLate();
        cleanup();
      }, CONNECT_TIMEOUT_MS);
    } catch (err) {
      if (attempt.cancelled) return;
      const denied = err && (err.name === "NotAllowedError" || err.name === "NotFoundError");
      setError(denied ? "Permití el micrófono en el navegador para atender llamadas" : formatApiErrorDetail(err.response?.data?.detail) || "No se pudo atender la llamada");
      cleanup();
    }
  };

  const reject = async (call) => {
    setRinging((r) => r.filter((x) => x.id !== call.id));
    try {
      await api.post(`/calls/${call.id}/reject`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const hangup = async () => {
    const current = activeRef.current;
    if (!current || closingRef.current) return;
    closingRef.current = true;
    release(); // cancela el intento (permiso, ICE, /answer pendientes) y suelta microfono, audio y conexion ya
    setError("");
    setActive((a) => (a ? { ...a, state: "closing" } : a));
    try {
      // el servidor decide segun el estado: si todavia suena la rechaza, si ya la tomaron la termina (solo quien la tomo)
      await api.post(`/calls/${current.id}/hangup`);
      cleanup();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 403) {
        // la tomo otra persona: no es de esta pantalla, solo se avisa
        cleanup();
        setError(detail || "Solo quien tomó la llamada puede cortarla");
      } else {
        // no se corto: la llamada sigue abierta y se puede reintentar; no se muestra como cerrada
        setActive((a) => (a && a.id === current.id ? { ...a, state: "close_failed" } : a));
        setError(detail ? `No se pudo cortar la llamada: ${detail}` : "No se pudo cortar la llamada. Probá de nuevo.");
      }
    } finally {
      closingRef.current = false;
    }
  };

  const toggleMute = () => {
    const next = !muted;
    (streamRef.current?.getAudioTracks() || []).forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  if (!enabled) return null;
  const call = ringing[0];
  if (!active && !call && !error) return <audio ref={audioRef} autoPlay className="hidden" />;
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-white shadow-lg p-4" data-testid={CONVERSATIONS.callCard}>
      <audio ref={audioRef} autoPlay className="hidden" />
      {active ? (
        <>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Llamada de WhatsApp</p>
          <p className="text-base font-medium text-foreground mt-0.5">{active.name || active.phone}</p>
          {active.name && <p className="text-xs text-muted-foreground">{active.phone}</p>}
          <p className="text-sm text-muted-foreground mt-2" data-testid={CONVERSATIONS.callStatus}>
            {active.state === "in_call" ? `En llamada · ${fmt(seconds)}` : CALL_STATE_TEXT[active.state] || "Conectando..."}
          </p>
          <div className="flex gap-2 mt-3">
            <Button type="button" variant="outline" className="flex-1 gap-1.5" onClick={toggleMute} disabled={active.state !== "in_call"} data-testid={CONVERSATIONS.callMuteButton}>
              {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />} {muted ? "Activar micrófono" : "Silenciar"}
            </Button>
            <Button type="button" className="flex-1 gap-1.5 bg-[#A34B49] hover:bg-[#8E3F3D] text-white" onClick={hangup} disabled={active.state === "closing"} data-testid={CONVERSATIONS.callHangupButton}>
              <PhoneOff className="w-4 h-4" /> Colgar
            </Button>
          </div>
        </>
      ) : call ? (
        <>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Llamada de WhatsApp entrante</p>
          <p className="text-base font-medium text-foreground mt-0.5">{call.name || call.phone}</p>
          {call.name && <p className="text-xs text-muted-foreground">{call.phone}</p>}
          {ringing.length > 1 && <p className="text-xs text-muted-foreground mt-1">y {ringing.length - 1} más esperando</p>}
          <div className="flex gap-2 mt-3">
            <Button type="button" variant="outline" className="flex-1 gap-1.5" onClick={() => reject(call)} data-testid={CONVERSATIONS.callRejectButton}>
              <PhoneOff className="w-4 h-4" /> Rechazar
            </Button>
            <Button type="button" className="flex-1 gap-1.5 bg-[#5B7A54] hover:bg-[#4C6746] text-white" onClick={() => answer(call)} data-testid={CONVERSATIONS.callAnswerButton}>
              <Phone className="w-4 h-4" /> Atender
            </Button>
          </div>
        </>
      ) : null}
      {error && (
        <div className="flex items-start justify-between gap-2 mt-2">
          <p className="text-xs text-destructive" data-testid={CONVERSATIONS.callError}>{error}</p>
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setError("")}>Cerrar</button>
        </div>
      )}
    </div>
  );
}
